import React from 'react';
import { trackGA4PreNav } from '@gumtree/shared/src/util/ga4-shared';

import { Button, Icon } from '..';

import {
    getNextPageUrl,
    getPaginationLinks,
    getPaginationUrl,
    getPreviousPageUrl,
} from './pagination-service';

import { qaAttribute } from '../utils/qa-service';
import { paginationCss } from './pagination.style';

export interface Page {
    pageNumber: number;
    pagePath: string;
}

export interface PaginationProps {
    currentPage: number;
    numberOfPages: number;
    pagePath: string;
    paginationSize?: number;
    firstPagePath?: string;
    onPrevClick?: (page: Page) => void;
    onNextClick?: (page: Page) => void;
    onPageClick?: (page: Page) => void;
}

const Pagination = ({
    currentPage,
    numberOfPages,
    pagePath,
    firstPagePath = '',
    paginationSize = 4,
    onPrevClick,
    onNextClick,
    onPageClick
}: PaginationProps) => {
    if (numberOfPages < currentPage) {
        return <></>;
    }

    const noPrevPage = !(currentPage > 1);
    const noNextPage = !(currentPage < numberOfPages);

    const previousPageUrl = getPreviousPageUrl({ pagePath, currentPage, firstPagePath });

    const nextPageUrl = getNextPageUrl({
        pagePath,
        currentPage,
        firstPagePath,
    });

    const handlePrevClick = async (event: React.MouseEvent<HTMLAnchorElement>) => {
        event.preventDefault();

        const page: Page = {
            pageNumber: currentPage - 1,
            pagePath: event.currentTarget.href,
        };

        trackGA4PreNav<GA4.PaginationInteractionEvent>({
            event: 'click_pagination',
            linkDomain: window.location.host,
            linkURL: pagePath,
            linkText: `prev: ${page.pageNumber}`,
        });

        onPrevClick?.(page);

        window.location.href = page.pagePath;
    };

    const handleNextClick = async (event: React.MouseEvent<HTMLAnchorElement>) => {
        event.preventDefault();

        const page: Page = {
            pageNumber: currentPage + 1,
            pagePath: event.currentTarget.href,
        };

        trackGA4PreNav<GA4.PaginationInteractionEvent>({
            event: 'click_pagination',
            linkDomain: window.location.host,
            linkURL: pagePath,
            linkText: `next: ${page.pageNumber}`,
        });

        onNextClick?.(page);

        window.location.href = page.pagePath;
    };

    const handlePageClick = async (event: React.MouseEvent<HTMLAnchorElement>) => {
        event.preventDefault();

        const pageNumber = parseInt(event.currentTarget.dataset.pageNumber ?? '-1');
        const page: Page = {
            pageNumber,
            pagePath: event.currentTarget.href,
        };

        trackGA4PreNav<GA4.PaginationInteractionEvent>({
            event: 'click_pagination',
            linkDomain: window.location.host,
            linkURL: pagePath,
            linkText: `${pageNumber}`,
        });

        onPageClick?.(page);

        window.location.href = page.pagePath;
    };

    return (
        <nav
            className="pagination"
            role="navigation"
            aria-label="Pagination Navigation"
            css={paginationCss}
        >
            <ol className="pagination-list">
                {numberOfPages > 1 && (
                    <li className="pagination-list-item">
                        <Button
                            className="pagination-link pagination-link--previous"
                            aria-label={`Go back to page ${currentPage - 1}`}
                            disabled={noPrevPage}
                            tabIndex={noPrevPage ? -1 : 0}
                            icon={<Icon type="chevron-l" className="pagination-icon" />}
                            href={noPrevPage ? undefined : previousPageUrl}
                            onClick={handlePrevClick}
                            {...qaAttribute('pagination-back-page')}
                        />
                    </li>
                )}
                {getPaginationLinks({ currentPage, numberOfPages, paginationSize }).map(
                    (link, index) => (
                        <React.Fragment key={link.pageNumber}>
                            {numberOfPages > paginationSize &&
                                index === 0 &&
                                link.pageNumber === numberOfPages - paginationSize + 1 && (
                                    <li
                                        className="pagination-eclipse"
                                        {...qaAttribute('pagination-ellipsis-back')}
                                    >
                                        ...
                                    </li>
                                )}
                            <li className="pagination-list-item">
                                <Button
                                    label={`${link.pageNumber}`}
                                    disabled={link.isActive}
                                    tabIndex={link.isActive ? -1 : 0}
                                    data-page-number={link.pageNumber}
                                    aria-label={
                                        link.isActive
                                            ? `current page, ${link.pageNumber}`
                                            : `View page ${link.pageNumber}`
                                    }
                                    aria-current={link.isActive}
                                    href={getPaginationUrl({
                                        pagePath,
                                        pageNumber: link.pageNumber,
                                        firstPagePath,
                                    })}
                                    className="pagination-link"
                                    onClick={handlePageClick}
                                    {...qaAttribute('pagination-page-number')}
                                />
                            </li>
                            {index === paginationSize - 1 && link.pageNumber < numberOfPages && (
                                <li
                                    className="pagination-eclipse"
                                    {...qaAttribute('pagination-ellipsis-forward')}
                                >
                                    ...
                                </li>
                            )}
                        </React.Fragment>
                    )
                )}
                {numberOfPages > 1 && (
                    <li className="pagination-list-item">
                        <Button
                            icon={<Icon type="chevron-r" className="pagination-icon" />}
                            aria-label={`Go to page ${currentPage + 1}`}
                            disabled={noNextPage}
                            tabIndex={noNextPage ? -1 : 0}
                            className="pagination-link pagination-link--next"
                            href={nextPageUrl}
                            onClick={handleNextClick}
                            {...qaAttribute('pagination-forward-page')}
                        />
                    </li>
                )}
            </ol>
        </nav>
    );
};

export default Pagination;
